import { type ReactNode, useCallback, useMemo, useState } from "react";

import { env } from "@/lib/env";

import {
  AuthContext,
  type AuthContextValue,
  type LoginOptions,
} from "./auth-context";
import { readAuthCookies } from "./auth-cookies";
import { buildAuthHeaders } from "./auth-headers";
import { buildLoginUrl, buildLogoutUrl } from "./auth-urls";
import { decodeIdToken, isExpired, toAuthUser } from "./id-token";

/**
 * SpiffArena backend を認証の当事者とする実装。
 *
 * - ログイン: `/v1.0/login` へフルページ遷移。backend が IdP へ飛ばし、
 *   `/v1.0/login_return` で access_token / id_token を cookie に set して戻す
 * - リクエスト: cookie を読んで Authorization: Bearer を付ける
 * - トークン更新: backend が refresh_token で行い新 cookie を返すため、
 *   フロント側にリフレッシュ処理は持たない
 *
 * cookie は同期で読めるため status が "loading" になることはないが、
 * Zeus IdP が非同期ブートストラップを要求する可能性を残して型には持たせている。
 */
export function SpiffAuthProvider({ children }: { children: ReactNode }) {
  const [cookies, setCookies] = useState(() =>
    readAuthCookies(document.cookie),
  );

  const login = useCallback(
    (options?: LoginOptions) => {
      window.location.href = buildLoginUrl({
        returnTo: options?.returnTo ?? window.location.href,
        authenticationIdentifier:
          options?.authenticationIdentifier ??
          cookies.authenticationIdentifier ??
          env.defaultAuthenticationIdentifier,
      });
    },
    [cookies.authenticationIdentifier],
  );

  const value = useMemo<AuthContextValue>(() => {
    const claims =
      cookies.idToken === null ? null : decodeIdToken(cookies.idToken);
    const isValid = claims !== null && !isExpired(claims, Date.now());

    return {
      status: isValid ? "authenticated" : "unauthenticated",
      user: claims !== null && isValid ? toAuthUser(claims) : null,
      login,
      logout: () => {
        window.location.href = buildLogoutUrl({
          idToken: cookies.idToken,
          authenticationIdentifier:
            cookies.authenticationIdentifier ??
            env.defaultAuthenticationIdentifier,
          redirectUrl: window.location.origin,
        });
      },
      onUnauthorized: () => {
        clearAuthCookies();
        setCookies(readAuthCookies(document.cookie));
        login();
      },
      // useMemo にキャプチャした古い cookie を使い続けないよう、毎回読み直す。
      // backend が API レスポンスで cookie を差し替えるため。
      getAuthHeaders: () => buildAuthHeaders(readAuthCookies(document.cookie)),
    };
  }, [cookies, login]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** 期限切れのセッションを掃除する。backend も logout で max_age=0 を返すが、401 時は自前で消す。 */
function clearAuthCookies(): void {
  for (const name of [
    "access_token",
    "id_token",
    "authentication_identifier",
  ]) {
    document.cookie = `${name}=; Max-Age=0; path=/`;
  }
}
